import { Box, Flex, Skeleton } from '@chakra-ui/react';
import { Button } from '@mesh.ai/rabbit';
import { useToast } from 'components/hooks/useToast';
import { format } from 'date-fns';
import { useOneOnOneAgendaQuery } from 'screens/oneOnOneRabbit/InstancePage/features/Agenda/hooks/dataHooks';
import { useOneOnOneInstanceDetailsQuery } from 'screens/oneOnOneRabbit/InstancePage/hooks/dataHooks';

import { BottomDrawer, DrawerContent, DrawerHeader, primaryDrawerWidth, DrawerFooter } from 'uiToolkitV1/BottomDrawer';
import { TextHeader4, TextLabel } from 'uiToolkitV1/Typography';

import { createPOAOneOnOneAgenda } from '../hooks/dataHooks';
import { AddAgenda } from './AddAgenda';
import { AgendaItem } from './AgendaItem';

interface DiscussOneOnOneSheetProps {
  onClose: () => void;
  revieweeName: string;
  reviewCycle: string;
  masterId: number;
  instanceId: number;
  actionPlanId: number;
  onAddAgenda?: () => void;
}

export const DiscussOneOnOneSheet = ({
  onClose,
  revieweeName,
  reviewCycle,
  masterId,
  instanceId,
  actionPlanId,
  onAddAgenda,
}: DiscussOneOnOneSheetProps) => {
  // to get start time
  const {
    data: oneOnOneInstanceDetails,
    isLoading: isLoadingOneOnOneInstanceDetails,
    isError: isErrorInOneOnOneInstanceDetails,
  } = useOneOnOneInstanceDetailsQuery({
    masterId,
    instanceId,
  });

  // to get agenda list
  const {
    data: oneOnOneAgendaList,
    isLoading: isLoadingOneOnOneAgendaList,
    isError: isErrorInOneOnOneAgendaList,
    refetch: refetchOneOnOneAgendaList,
  } = useOneOnOneAgendaQuery({
    masterId,
    instanceId,
  });

  const toast = useToast();

  if (isErrorInOneOnOneAgendaList || isErrorInOneOnOneInstanceDetails) {
    toast({
      title: 'An unknown error has occurred, please try again',
      status: 'error',
    });

    return null;
  }

  let formattedStartTime: string = '';
  if (oneOnOneInstanceDetails?.startTimeTs) {
    const startTime = new Date(oneOnOneInstanceDetails.startTimeTs * 1000);
    formattedStartTime = format(startTime, 'MMM d, yyyy • h:mm a');
  }

  const handleAddAgenda = async (agendaText: string) => {
    try {
      await createPOAOneOnOneAgenda({
        newAgenda: { agendaText, referenceType: null },
        reviewCycle,
        actionPlanId,
        instanceId,
      });

      if (onAddAgenda) onAddAgenda();

      toast({
        title: 'Agenda added successfully',
        status: 'success',
      });

      refetchOneOnOneAgendaList();
    } catch (err) {
      toast({
        title: 'There was an error adding the agenda, please try again',
        status: 'error',
      });

      console.log('Error in adding agenda', err);
    }
  };

  return (
    <BottomDrawer isOpen onClose={onClose}>
      <DrawerContent width={`${primaryDrawerWidth}px`}>
        <DrawerHeader onClose={onClose}>{undefined}</DrawerHeader>
        <Box h="100%" p="4px" borderRadius="8px" bg="white">
          <Box borderRadius="8px" bg="#CFD7DF" h="100%" width="100%" p="4px">
            <Box bg="white" borderBottom="1px solid #CFD7DF" h="100%" width="100%">
              <Flex flexDirection="column" height="100%" borderRadius="8px 8px 0 0">
                {/* Header */}
                <Box p="4px 8px" m="8px 12px" borderBottom="1px solid #CFD7DF">
                  <TextHeader4 bold padding="4px">
                    Next 1:1 with {revieweeName}
                  </TextHeader4>
                  <Flex alignItems="center" padding="4px">
                    {isLoadingOneOnOneInstanceDetails ? (
                      <Skeleton height="16px" width="150px" borderRadius="4px" />
                    ) : (
                      <TextLabel color="text.g2">{formattedStartTime}</TextLabel>
                    )}
                  </Flex>
                </Box>
                {/* Main */}
                <Box flex="1" overflow="auto" mx="12px">
                  <Flex height="20px" alignItems="center" pl="12px">
                    <TextLabel color="text.g2" bold>
                      Agenda:
                    </TextLabel>
                  </Flex>
                  <Box>
                    {isLoadingOneOnOneAgendaList && (
                      <Flex flexDir="column" gap="8px" p="8px">
                        <Skeleton height="80px" width="100%" borderRadius="4px" />
                        <Skeleton height="80px" width="100%" borderRadius="4px" />
                        <Skeleton height="80px" width="100%" borderRadius="4px" />
                        <Skeleton height="80px" width="100%" borderRadius="4px" />
                        <Skeleton height="80px" width="100%" borderRadius="4px" />
                      </Flex>
                    )}
                    {oneOnOneAgendaList &&
                      oneOnOneAgendaList.map((agenda) => (
                        <AgendaItem
                          key={agenda.instanceDataId}
                          title={agenda.agendaText}
                          ownerName={agenda?.ownerDetails?.displayName}
                          ownerProfilePic={agenda?.ownerDetails?.profilePhoto}
                        />
                      ))}
                  </Box>
                  <AddAgenda onAddAgenda={handleAddAgenda} />
                </Box>
                {/* Footer */}
                <Box borderRadius="0 0 8px 8px" overflow="hidden">
                  <DrawerFooter backgroundColor="#EBEFF3">
                    <Button variant="filled" onClick={onClose} disabled={false}>
                      Close
                    </Button>
                  </DrawerFooter>
                </Box>
              </Flex>
            </Box>
          </Box>
        </Box>
      </DrawerContent>
    </BottomDrawer>
  );
};
